// src/pages/Documents.jsx
import { useState, useEffect, useMemo } from "react";
import { Upload, Eye, Send, X, ChevronDown, Filter, Trash2 } from "lucide-react";

import {
  uploadDocumentFile,
  createDocument,
  listDocuments,
  deleteDocument,
  getDocumentOpenUrl,
  assignDocument,
} from "../api/documents";
import { listAllTenants } from "../api/tenants";
import { useSubmissionLock } from "../hooks/useSubmissionLock";

const DOCS_PER_PAGE = 25;

function formatDateISO(dateStr) {
  try {
    const d = new Date(dateStr);
    if (Number.isNaN(d.getTime())) return dateStr || "";
    return d.toISOString().slice(0, 10);
  } catch {
    return dateStr || "";
  }
}

export default function Documents() {
  const [documents, setDocuments] = useState([]);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState("");
  const [showFlyout, setShowFlyout] = useState(false);
  const [selectedDocs, setSelectedDocs] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [tenants, setTenants] = useState([]);
  const [selectedTenantId, setSelectedTenantId] = useState("");
  const [notes, setNotes] = useState("");
  const [totalDocs, setTotalDocs] = useState(0);
  const [error, setError] = useState("");
  const { run: runSubmission, isPending } = useSubmissionLock();

  const [viewerOpen, setViewerOpen] = useState(false);
  const [viewerUrl, setViewerUrl] = useState("");
  const [viewerTitle, setViewerTitle] = useState("");

  async function loadDocs() {
    setError("");
    try {
      const data = await listDocuments();
      setDocuments(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error("[Documents] list error:", e);
      setError(e.message || "Failed to load documents");
      setDocuments([]);
    }
  }

  useEffect(() => {
    loadDocs();
  }, []);

  const filteredDocs = useMemo(() => {
    let docs = documents;

    if (search) {
      const q = search.toLowerCase();
      docs = docs.filter((doc) => (doc.title || "").toLowerCase().includes(q));
    }

    if (sort === "az") {
      docs = [...docs].sort((a, b) => (a.title || "").localeCompare(b.title || ""));
    } else if (sort === "za") {
      docs = [...docs].sort((a, b) => (b.title || "").localeCompare(a.title || ""));
    } else if (sort === "latest") {
      docs = [...docs].sort((a, b) => String(b.createdAt || "").localeCompare(String(a.createdAt || "")));
    } else if (sort === "earliest") {
      docs = [...docs].sort((a, b) => String(a.createdAt || "").localeCompare(String(b.createdAt || "")));
    }

    return docs;
  }, [documents, search, sort]);

  useEffect(() => {
    setTotalDocs(filteredDocs.length);
    setPage(1);
  }, [search, sort, documents, filteredDocs.length]);

  const totalPages = Math.max(1, Math.ceil(totalDocs / DOCS_PER_PAGE));
  const pagedDocs = filteredDocs.slice((page - 1) * DOCS_PER_PAGE, page * DOCS_PER_PAGE);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (uploading || isPending("document:upload")) return;

    await runSubmission("document:upload", async () => {
      setUploading(true);
      setError("");

      try {
        const uploaded = await uploadDocumentFile(file);
        await createDocument({
          title: file.name,
          url: uploaded.url,
          type: "OTHER",
          propertyId: null,
        });

        await loadDocs();
      } catch (err) {
        console.error("[Documents] upload error:", err);
        setError(err.message || "Upload failed");
      } finally {
        setUploading(false);
        e.target.value = "";
      }
    });
  };

  const handleSelectDoc = (id) => {
    setSelectedDocs((prev) => (prev.includes(id) ? prev.filter((docId) => docId !== id) : [...prev, id]));
  };

  const openViewer = async (doc) => {
    setError("");
    try {
      const url = await getDocumentOpenUrl(doc.id);
      if (!url) throw new Error("Document file not found");
      setViewerUrl(url);
      setViewerTitle(doc.title || "Document");
      setViewerOpen(true);
    } catch (e) {
      console.error("[Documents] open error:", e);
      setError(e.message || "Failed to open document");
    }
  };

  const handleDelete = async (id) => {
    await runSubmission(`document:${id}:delete`, async () => {
      if (!window.confirm("Delete this document?")) return;
      setError("");
      try {
        await deleteDocument(id);
        await loadDocs();
        setSelectedDocs((prev) => prev.filter((d) => d !== id));
      } catch (e) {
        console.error("[Documents] delete error:", e);
        setError(e.message || "Delete failed");
      }
    });
  };

  const openSendFlyout = async (preselectDocId) => {
    try {
      if (preselectDocId) setSelectedDocs([preselectDocId]);

      const tntsRes = await listAllTenants();
      const tnts =
        tntsRes?.data?.tenants ||
        tntsRes?.data ||
        tntsRes?.tenants ||
        tntsRes ||
        [];

      setTenants(Array.isArray(tnts) ? tnts : []);
      setSelectedTenantId("");
      setNotes("");
      setShowFlyout(true);
    } catch (e) {
      console.error("[Documents] load tenants error:", e);
      setTenants([]);
      setShowFlyout(true);
    }
  };

  const handleFlyoutSubmit = async (e) => {
    e.preventDefault();
    if (isPending("document:send")) return;
    if (!selectedTenantId || selectedDocs.length === 0) return;

    await runSubmission("document:send", async () => {
      try {
        for (const docId of selectedDocs) {
          await assignDocument(docId, selectedTenantId);
        }
        setShowFlyout(false);
        setSelectedDocs([]);
        setSelectedTenantId("");
        setNotes("");
      } catch (err) {
        console.error("[Documents] assign error:", err);
        alert(err.message || "Failed to send document");
      }
    });
  };

  return (
    <div className="pt-10 md:pt-0">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-4xl text-[#ffffff]">Your documents</h1>
        <label className="flex items-center gap-2 px-4 py-2 bg-white/10 text-white border border-white/10 rounded-xl hover:bg-[#AF9B8A]/20 transition backdrop-blur-md cursor-pointer">
          <Upload className="w-5 h-5" />
          <span>{uploading ? "Uploading..." : "Upload"}</span>
          <input
            type="file"
            className="hidden"
            onChange={handleUpload}
            disabled={uploading}
            accept="application/pdf,image/*"
          />
        </label>
      </div>
      <div className="h-px w-36 bg-[#AF9B8A] rounded-full mb-6"></div>

      {error && (
        <div className="mb-4 px-4 py-3 rounded-lg bg-rose-900/40 border border-rose-700/40 text-rose-200">
          {error}
        </div>
      )}

      <div className="mb-6 flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:gap-4 sm:items-center">
        <input
          type="text"
          placeholder="Search by document name..."
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(1);
          }}
          className="px-4 py-2 rounded-lg bg-white/10 border border-white/10 placeholder-gray-400 text-white backdrop-blur-md w-full sm:w-auto"
        />

        <div className="flex gap-2 w-full sm:w-auto">
          <div className="relative flex-1">
            <select
              className="appearance-none w-full p-3 rounded-lg text-white bg-white/5 border border-white/10 focus:outline-none focus:ring-2 focus:ring-[#AF9B8A] glass"
              style={{ cursor: "pointer" }}
              value={sort}
              onChange={(e) => setSort(e.target.value)}
            >
              <option value="">Sort</option>
              <option value="az">A - Z</option>
              <option value="za">Z - A</option>
              <option value="latest">Date Uploaded: Latest</option>
              <option value="earliest">Date Uploaded: Earliest</option>
            </select>
            <ChevronDown className="absolute right-4 top-1/2 -translate-y-1/2 w-4 h-4 pointer-events-none text-white/70" />
          </div>
          <button
            type="button"
            className="flex items-center gap-2 px-4 py-2 bg-white/10 text-white border border-white/10 rounded-xl hover:bg-[#AF9B8A]/20 transition backdrop-blur-md cursor-pointer"
            onClick={() => {}}
          >
            <Filter className="w-4 h-4" />
            <span>Apply Filters</span>
          </button>
        </div>
      </div>

      <div className="space-y-4 mb-8">
        <div className="hidden sm:block">
          {pagedDocs.map((doc) => (
            <div
              key={doc.id}
              className="flex flex-row items-center justify-between bg-white/10 border border-white/10 rounded-xl px-4 py-3 gap-2 shadow backdrop-blur-md mb-4"
            >
              <div className="flex items-center gap-3 flex-1">
                <input
                  type="checkbox"
                  checked={selectedDocs.includes(doc.id)}
                  onChange={() => handleSelectDoc(doc.id)}
                  className="accent-[#AF9B8A] w-4 h-4"
                />
                <div>
                  <div className="font-semibold text-white">{doc.title || doc.name || "Untitled"}</div>
                  <div className="text-xs text-gray-400">{formatDateISO(doc.createdAt) || ""}</div>
                </div>
              </div>
              <div className="flex gap-2">
                <button
                  className="flex items-center gap-1 px-3 py-1 rounded-lg bg-white/10 hover:bg-[#AF9B8A]/20 transition text-white"
                  onClick={() => openViewer(doc)}
                >
                  <Eye className="w-4 h-4" />
                  <span className="hidden sm:inline">View</span>
                </button>
                <button
                  className="flex items-center gap-1 px-3 py-1 rounded-lg bg-white/10 hover:bg-[#AF9B8A]/20 transition text-white"
                  onClick={() => openSendFlyout(doc.id)}
                  disabled={isPending("document:send")}
                >
                  <Send className="w-4 h-4" />
                  <span className="hidden sm:inline">Send</span>
                </button>
                <button
                  className="flex items-center gap-1 px-3 py-1 rounded-lg bg-white/10 hover:bg-[#AF9B8A]/20 transition text-white disabled:opacity-50"
                  onClick={() => handleDelete(doc.id)}
                  disabled={isPending(`document:${doc.id}:delete`)}
                  aria-busy={isPending(`document:${doc.id}:delete`)}
                >
                  <Trash2 className="w-4 h-4" />
                  <span className="hidden sm:inline">{isPending(`document:${doc.id}:delete`) ? "Deleting..." : "Delete"}</span>
                </button>
              </div>
            </div>
          ))}
        </div>

        <div className="sm:hidden flex flex-col gap-3">
          {pagedDocs.map((doc) => (
            <div
              key={doc.id}
              className="flex flex-col gap-2 bg-white/10 border border-white/10 rounded-xl p-3 shadow backdrop-blur-md"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={selectedDocs.includes(doc.id)}
                    onChange={() => handleSelectDoc(doc.id)}
                    className="accent-[#AF9B8A] w-4 h-4"
                  />
                  <span className="font-semibold text-white text-sm">
                    {doc.title || doc.name || "Untitled"}
                  </span>
                </div>
                <span className="text-xs text-gray-400">{formatDateISO(doc.createdAt) || ""}</span>
              </div>
              <div className="flex gap-2 mt-1">
                <button
                  className="flex-1 flex items-center justify-center gap-1 px-2 py-1 rounded-lg bg-white/10 hover:bg-[#AF9B8A]/20 transition text-xs text-white"
                  onClick={() => openViewer(doc)}
                >
                  <Eye className="w-4 h-4" />
                  View
                </button>
                <button
                  className="flex-1 flex items-center justify-center gap-1 px-2 py-1 rounded-lg bg-white/10 hover:bg-[#AF9B8A]/20 transition text-xs text-white"
                  onClick={() => openSendFlyout(doc.id)}
                >
                  <Send className="w-4 h-4" />
                  Send
                </button>
                <button
                  className="flex-1 flex items-center justify-center gap-1 px-2 py-1 rounded-lg bg-white/10 hover:bg-[#AF9B8A]/20 transition text-xs text-white disabled:opacity-50"
                  onClick={() => handleDelete(doc.id)}
                  disabled={isPending(`document:${doc.id}:delete`)}
                  aria-busy={isPending(`document:${doc.id}:delete`)}
                >
                  <Trash2 className="w-4 h-4" />
                  {isPending(`document:${doc.id}:delete`) ? "Deleting..." : "Delete"}
                </button>
              </div>
            </div>
          ))}
        </div>

        {pagedDocs.length === 0 && (
          <div className="col-span-full text-center text-white opacity-60 py-10">No documents found.</div>
        )}
      </div>

      <div className="flex items-center justify-between text-white opacity-70 text-sm mb-12">
        <div>{totalDocs} documents</div>
        <div className="flex items-center gap-4">
          <button
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page === 1}
            className="opacity-70 hover:opacity-100 transition disabled:opacity-40"
            aria-label="Previous page"
          >
            Prev
          </button>
          <span>
            {page} / {totalPages}
          </span>
          <button
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
            className="opacity-70 hover:opacity-100 transition disabled:opacity-40"
            aria-label="Next page"
          >
            Next
          </button>
        </div>
      </div>

      {viewerOpen && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50">
          <div className="bg-[#141414] w-[90vw] h-[85vh] rounded-xl overflow-hidden border border-white/10 shadow-xl flex flex-col">
            <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
              <div className="font-semibold text-white">{viewerTitle}</div>
              <button
                onClick={() => setViewerOpen(false)}
                className="px-3 py-1 rounded bg-white/10 hover:bg-white/20 text-white"
              >
                Close
              </button>
            </div>
            <div className="flex-1 bg-black">
              <iframe title="Document Viewer" src={viewerUrl} className="w-full h-full" />
            </div>
          </div>
        </div>
      )}

      {showFlyout && (
        <div className="fixed inset-0 bg-black bg-opacity-80 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-[#141414] max-w-md w-full rounded-xl p-6 shadow-lg relative text-white">
            <button
              onClick={() => setShowFlyout(false)}
              className="absolute top-3 right-3 p-1 rounded-full hover:bg-white/20"
              aria-label="Close send panel"
            >
              <X className="w-5 h-5" />
            </button>
            <h2 className="text-xl font-semibold mb-4">Send documents</h2>
            <form onSubmit={handleFlyoutSubmit} className="flex flex-col gap-4">
              <div className="text-sm text-white/70">
                {selectedDocs.length} document{selectedDocs.length !== 1 ? "s" : ""} selected
              </div>

              <label className="flex flex-col text-sm">
                Select recipient
                <select
                  value={selectedTenantId}
                  onChange={(e) => setSelectedTenantId(e.target.value)}
                  required
                  className="mt-1 p-2 rounded-lg bg-white/10 border border-white/10 text-white"
                >
                  <option value="" disabled>
                    Choose a tenant
                  </option>
                  {tenants.length === 0 && (
                    <option value="" disabled>
                      No tenants found
                    </option>
                  )}
                  {tenants.map((t) => {
                    const label =
                      t.name ||
                      `${(t.firstName || "").trim()} ${(t.lastName || "").trim()}`.trim() ||
                      t.email ||
                      t.id;
                    return (
                      <option key={t.id} value={t.id}>
                        {label}{t.email ? ` (${t.email})` : ""}
                      </option>
                    );
                  })}
                </select>
              </label>

              <label className="flex flex-col text-sm">
                Notes
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={3}
                  placeholder="Add optional notes…"
                  className="mt-1 p-2 rounded-lg bg-white/10 border border-white/10 text-white resize-none"
                />
              </label>

              <div className="flex justify-end gap-4 pt-2 border-t border-white/10">
                <button
                  type="button"
                  onClick={() => setShowFlyout(false)}
                  className="px-4 py-2 rounded-lg border border-white/10 text-white hover:bg-white/10 transition"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={!selectedTenantId || selectedDocs.length === 0 || isPending("document:send")}
                  className="px-4 py-2 rounded-lg bg-[#AF9B8A] text-white hover:bg-[#97856f] transition disabled:opacity-50"
                  aria-busy={isPending("document:send")}
                >
                  {isPending("document:send") ? "Sending..." : "Send"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
